import { motion } from "framer-motion";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import {
    MdGroups,
    MdRefresh,
    MdPerson,
    MdLayers,
    MdAccountBalance,
    MdSavings,
    MdHourglassEmpty,
} from "react-icons/md";
import { BASE_API_URL } from "../helpers/Config";
import { getPageStyles } from "../helpers/pageTheme";

const HERO_GRADIENT =
    "linear-gradient(135deg, #059669 0%, #047857 55%, #065f46 100%)";

const LEVEL_FILTERS = [
    { value: "all", label: "All" },
    ...Array.from({ length: 10 }, (_, i) => ({
        value: String(i + 1),
        label: `L${i + 1}`,
    })),
];

const getApiError = (payload) => {
    if (!payload) return "Invalid response";
    if (payload.error === "session expired") return "session expired";
    if (payload.error === false || payload.error === null || payload.error === undefined) {
        return null;
    }
    if (payload.error === true) return payload?.message || "Request failed";
    if (typeof payload.error === "string") return payload.error;
    return null;
};

const formatCurrency = (amount) => {
    const n = Number(amount);
    if (isNaN(n)) return "$0.00";
    return `$${n.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
};

const AMOUNT_FIELDS = [
    { key: "total", label: "Total", icon: MdSavings, accent: "#059669" },
    { key: "active", label: "Active", icon: MdAccountBalance, accent: "#6366f1" },
    { key: "pending", label: "Pending", icon: MdHourglassEmpty, accent: "#f59e0b" },
];

const TeamMemberCard = ({ member, isDark, styles }) => {
    const levelNum = Number(member.level);

    return (
        <article
            style={{
                ...styles.card,
                padding: 0,
                borderLeft: "3px solid #059669",
            }}
        >
            <div style={{ padding: "14px 16px" }}>
                <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                    <div className="d-flex align-items-center gap-2 min-w-0">
                        <div
                            className="d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{
                                width: 36,
                                height: 36,
                                borderRadius: "10px",
                                backgroundColor: isDark
                                    ? "rgba(5, 150, 105, 0.2)"
                                    : "#ecfdf5",
                            }}
                        >
                            <MdPerson size={20} style={{ color: "#059669" }} />
                        </div>
                        <p
                            className="mb-0 fw-bold text-truncate min-w-0"
                            style={{ fontSize: "0.9rem" }}
                        >
                            ID: {member.id ?? "—"}
                        </p>
                    </div>
                    <span
                        className="d-inline-block rounded-pill flex-shrink-0"
                        style={{
                            fontSize: "0.72rem",
                            fontWeight: 700,
                            padding: "5px 12px",
                            backgroundColor: isDark
                                ? "rgba(99, 102, 241, 0.18)"
                                : "#eef2ff",
                            color: isDark ? "#a5b4fc" : "#4f46e5",
                            border: `1px solid ${isDark ? "rgba(129, 140, 248, 0.35)" : "#c7d2fe"
                                }`,
                        }}
                    >
                        L{!isNaN(levelNum) ? levelNum : "—"}
                    </span>
                </div>

                <div className="row g-2">
                    {AMOUNT_FIELDS.map(({ key, label, icon: Icon, accent }) => (
                        <div key={key} className="col-4">
                            <div
                                className="p-2 h-100 rounded-2"
                                style={{
                                    backgroundColor: isDark ? "#0f172a" : "#f8fafc",
                                    border: `1px solid ${isDark ? "#1f2937" : "#e5e7eb"}`,
                                }}
                            >
                                <div className="d-flex align-items-center gap-1 mb-1">
                                    <Icon size={14} style={{ color: accent }} />
                                    <span
                                        className="text-uppercase"
                                        style={{
                                            fontSize: "0.52rem",
                                            fontWeight: 700,
                                            color: styles.textMuted,
                                        }}
                                    >
                                        {label}
                                    </span>
                                </div>
                                <p
                                    className="mb-0 fw-bold"
                                    style={{
                                        fontSize: "0.82rem",
                                        color: accent,
                                        fontVariantNumeric: "tabular-nums",
                                    }}
                                >
                                    {formatCurrency(member[key])}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </article>
    );
};

const RecoveryTradeTeam = ({ theme = "dark" }) => {
    const [members, setMembers] = useState([]);
    const [levelFilter, setLevelFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    const navigate = useNavigate();
    const fetchIdRef = useRef(0);
    const isDark = theme === "dark";
    const styles = useMemo(() => getPageStyles(isDark), [isDark]);

    const fetchTeamReport = useCallback(
        async ({ isRefresh = false, level = levelFilter } = {}) => {
            const token = localStorage.getItem("a5srf4wer7w1cd");
            if (!token) {
                setError("Please login again");
                setLoading(false);
                return;
            }

            const requestId = ++fetchIdRef.current;

            if (isRefresh) setRefreshing(true);
            else setLoading(true);
            setError(null);

            const body = { token };
            if (level !== "all") {
                body.level = Number(level);
            } else {
                body.level = null;
            }

            try {
                const response = await axios.post(
                    `${BASE_API_URL}/recovery-trade/team-report`,
                    body
                );
                if (requestId !== fetchIdRef.current) return;

                const payload = response?.data;
                const apiError = getApiError(payload);

                if (apiError === "session expired") {
                    localStorage.removeItem("a5srf4wer7w1cd");
                    navigate("/login");
                    return;
                }

                if (apiError) {
                    setError(apiError);
                    toast.error(apiError);
                    return;
                }

                const list = Array.isArray(payload?.data) ? payload.data : [];
                setMembers(list);
            } catch (err) {
                if (requestId !== fetchIdRef.current) return;
                const raw =
                    err.response?.data?.error || err.response?.data?.message;
                const errText =
                    typeof raw === "string"
                        ? raw
                        : "Failed to fetch recovery team report";
                setError(errText);
                toast.error(errText);
            } finally {
                if (requestId !== fetchIdRef.current) return;
                setLoading(false);
                setRefreshing(false);
            }
        },
        [navigate, levelFilter]
    );

    useEffect(() => {
        fetchTeamReport({ level: levelFilter });
    }, [levelFilter, fetchTeamReport]);

    const handleRefresh = () =>
        fetchTeamReport({ isRefresh: true, level: levelFilter });

    const summary = useMemo(() => {
        let sumTotal = 0;
        let sumActive = 0;
        let sumPending = 0;
        members.forEach((m) => {
            sumTotal += Number(m.total) || 0;
            sumActive += Number(m.active) || 0;
            sumPending += Number(m.pending) || 0;
        });
        return {
            count: members.length,
            sumTotal,
            sumActive,
            sumPending,
        };
    }, [members]);

    const groupedByLevel = useMemo(() => {
        if (levelFilter !== "all") return null;
        const map = new Map();
        members.forEach((m) => {
            const lvl = Number(m.level);
            const key = isNaN(lvl) ? 0 : lvl;
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(m);
        });
        return Array.from(map.entries()).sort((a, b) => a[0] - b[0]);
    }, [members, levelFilter]);

    const renderMemberList = (list) => (
        <div className="d-flex flex-column gap-2">
            {list.map((member, index) => (
                <TeamMemberCard
                    key={`${member.id}-${member.level}-${index}`}
                    member={member}
                    isDark={isDark}
                    styles={styles}
                />
            ))}
        </div>
    );

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.35 }}
            className="recovery-team-page d-flex flex-column h-100"
            style={{
                ...styles.page,
                maxWidth: 480,
                margin: "0 auto",
                overflow: "hidden",
            }}
        >
            <div
                className="flex-shrink-0"
                style={{
                    zIndex: 2,
                    backgroundColor: styles.page.backgroundColor,
                    borderBottom: `1px solid ${isDark ? "#1f2937" : "#e5e7eb"}`,
                    paddingBottom: "10px",
                }}
            >
                <div style={{ ...styles.hero(HERO_GRADIENT), padding: "12px 14px" }}>
                    <div className="d-flex justify-content-between align-items-start gap-2">
                        <div>
                            <h6
                                className="fw-bold mb-0 d-flex align-items-center gap-1"
                                style={{ fontSize: "0.95rem" }}
                            >
                                <MdGroups size={20} />
                                Recovery Team
                            </h6>
                            <p className="mb-0 opacity-75" style={{ fontSize: "0.68rem" }}>
                                {summary.count > 0
                                    ? `${summary.count} member${summary.count !== 1 ? "s" : ""} · downline recovery totals`
                                    : "Downline total, active & pending recovery"}
                            </p>
                        </div>
                        <motion.button
                            type="button"
                            className="btn btn-sm d-inline-flex align-items-center justify-content-center flex-shrink-0 border-0 p-0"
                            style={{
                                width: 40,
                                height: 40,
                                background: "rgba(255,255,255,0.2)",
                                border: "1px solid rgba(255,255,255,0.25)",
                                color: "#fff",
                                borderRadius: "10px",
                            }}
                            whileTap={{ scale: 0.96 }}
                            onClick={handleRefresh}
                            disabled={loading || refreshing}
                            aria-label="Refresh"
                        >
                            <motion.span
                                className="d-inline-flex align-items-center justify-content-center"
                                style={{ lineHeight: 0 }}
                                animate={{ rotate: refreshing ? 360 : 0 }}
                                transition={{
                                    duration: 0.8,
                                    repeat: refreshing ? Infinity : 0,
                                    ease: "linear",
                                }}
                            >
                                <MdRefresh size={20} />
                            </motion.span>
                        </motion.button>
                    </div>
                </div>

                <div className="pt-2">
                    <p
                        className="mb-1 px-1"
                        style={{ ...styles.sectionTitle, fontSize: "0.65rem" }}
                    >
                        Filter by level
                    </p>
                    <div
                        className="d-flex gap-1 rt-team-filters"
                        style={{
                            overflowX: "auto",
                            scrollbarWidth: "none",
                            msOverflowStyle: "none",
                        }}
                    >
                        {LEVEL_FILTERS.map(({ value, label }) => (
                            <motion.button
                                key={value}
                                type="button"
                                className="btn btn-sm flex-shrink-0 border-0"
                                style={{
                                    ...styles.filterChip(levelFilter === value),
                                    ...(levelFilter === value
                                        ? {
                                            background:
                                                "linear-gradient(135deg, #059669 0%, #047857 100%)",
                                        }
                                        : {}),
                                }}
                                whileTap={{ scale: 0.97 }}
                                onClick={() => setLevelFilter(value)}
                                disabled={loading && !refreshing}
                            >
                                {label}
                            </motion.button>
                        ))}
                    </div>
                </div>
            </div>

            <div className="flex-grow-1 overflow-auto min-h-0 rt-team-scroll pt-2 pb-3">
                {!loading && !error && members.length > 0 && (
                    <div className="row g-2 mb-2">
                        <div className="col-3">
                            <div
                                className="text-center py-2 px-1 rounded-3 h-100"
                                style={{
                                    ...styles.card,
                                    padding: "10px 6px",
                                }}
                            >
                                <p
                                    className="mb-0 text-uppercase"
                                    style={{
                                        fontSize: "0.5rem",
                                        fontWeight: 700,
                                        color: styles.textMuted,
                                    }}
                                >
                                    Members
                                </p>
                                <p
                                    className="mb-0 fw-bold"
                                    style={{ fontSize: "0.95rem", color: "#059669" }}
                                >
                                    {summary.count}
                                </p>
                            </div>
                        </div>
                        <div className="col-3">
                            <div
                                className="text-center py-2 px-1 rounded-3 h-100"
                                style={{
                                    ...styles.card,
                                    padding: "10px 6px",
                                }}
                            >
                                <p
                                    className="mb-0 text-uppercase"
                                    style={{
                                        fontSize: "0.5rem",
                                        fontWeight: 700,
                                        color: styles.textMuted,
                                    }}
                                >
                                    Total
                                </p>
                                <p
                                    className="mb-0 fw-bold text-truncate"
                                    style={{
                                        fontSize: "0.72rem",
                                        color: "#059669",
                                    }}
                                >
                                    {formatCurrency(summary.sumTotal)}
                                </p>
                            </div>
                        </div>
                        <div className="col-3">
                            <div
                                className="text-center py-2 px-1 rounded-3 h-100"
                                style={{
                                    ...styles.card,
                                    padding: "10px 6px",
                                }}
                            >
                                <p
                                    className="mb-0 text-uppercase"
                                    style={{
                                        fontSize: "0.5rem",
                                        fontWeight: 700,
                                        color: styles.textMuted,
                                    }}
                                >
                                    Active
                                </p>
                                <p
                                    className="mb-0 fw-bold text-truncate"
                                    style={{
                                        fontSize: "0.72rem",
                                        color: "#6366f1",
                                    }}
                                >
                                    {formatCurrency(summary.sumActive)}
                                </p>
                            </div>
                        </div>
                        <div className="col-3">
                            <div
                                className="text-center py-2 px-1 rounded-3 h-100"
                                style={{
                                    ...styles.card,
                                    padding: "10px 6px",
                                }}
                            >
                                <p
                                    className="mb-0 text-uppercase"
                                    style={{
                                        fontSize: "0.5rem",
                                        fontWeight: 700,
                                        color: styles.textMuted,
                                    }}
                                >
                                    Pending
                                </p>
                                <p
                                    className="mb-0 fw-bold text-truncate"
                                    style={{
                                        fontSize: "0.72rem",
                                        color: "#f59e0b",
                                    }}
                                >
                                    {formatCurrency(summary.sumPending)}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {error && !loading && (
                    <div className="p-2 text-center" style={styles.card}>
                        <p className="mb-2 small" style={{ color: styles.textMuted }}>
                            {error}
                        </p>
                        <motion.button
                            type="button"
                            className="btn btn-sm"
                            style={{
                                background:
                                    "linear-gradient(135deg, #059669 0%, #047857 100%)",
                                color: "#fff",
                                fontWeight: 700,
                            }}
                            whileTap={{ scale: 0.98 }}
                            onClick={handleRefresh}
                        >
                            Try again
                        </motion.button>
                    </div>
                )}

                {loading && (
                    <div className="text-center py-4">
                        <div
                            className="spinner-border spinner-border-sm text-success"
                            role="status"
                        />
                        <p
                            className="mb-0 mt-2 small"
                            style={{ color: styles.textMuted }}
                        >
                            Loading team report…
                        </p>
                    </div>
                )}

                {!loading && !error && members.length === 0 && (
                    <div style={styles.empty}>
                        <MdGroups size={36} className="mb-2 opacity-50" />
                        <p className="mb-0 fw-semibold small">
                            No downlines at this level
                        </p>
                        <p
                            className="mb-0 small mt-1 px-3"
                            style={{ color: styles.textMuted }}
                        >
                            {levelFilter === "all"
                                ? "Your recovery team tree has no members yet."
                                : `No level ${levelFilter} downlines with recovery data.`}
                        </p>
                    </div>
                )}

                {!loading && !error && members.length > 0 && (
                    <div className="d-flex flex-column gap-2">
                        {groupedByLevel
                            ? groupedByLevel.map(([level, list]) => (
                                <section key={level}>
                                    <p
                                        className="mb-2 mt-0 px-1 d-flex align-items-center gap-1"
                                        style={styles.sectionTitle}
                                    >
                                        <MdLayers size={14} />
                                        Level {level}
                                        <span
                                            className="ms-1 fw-normal opacity-75"
                                            style={{ textTransform: "none" }}
                                        >
                                            ({list.length})
                                        </span>
                                    </p>
                                    {renderMemberList(list)}
                                </section>
                            ))
                            : renderMemberList(members)}
                    </div>
                )}
            </div>

            <style>{`
                .rt-team-scroll {
                    scrollbar-width: none;
                    -ms-overflow-style: none;
                }
                .rt-team-scroll::-webkit-scrollbar {
                    display: none;
                }
                .rt-team-filters::-webkit-scrollbar {
                    display: none;
                }
            `}</style>
        </motion.div>
    );
};

export default RecoveryTradeTeam;
